<template>
  <div class="bom-view--single-level-view">
    <div v-if="problemProsecutorResults">
      <div v-for="(items, checker) in problemProsecutorResults" :key="checker">
        <Alert :type="item.status === 'warn' ? 'warning' : item.status" v-for="item in items" :key="`${checker}-${item.code}`" closable>
          {{ item.code }}
          <template slot="desc">
            <a @click="handleProsecutorDetailDisplay(item.code)">
              {{ $t(`bom_tool.prosecutor_code_message.${item.code}`) }} ({{ _.filter(item.extra_data).length }})
            </a>
            <div v-show="showProsecutorDetail[item.code]" style="padding-left: 28px; margin-top: 5px;">
              <ol>
                <li v-for="it in _.filter(item.extra_data)" :key="it.id">{{ it.part_number }} {{ it.chinese_name || it.name }} {{ it.mpn }}</li>
              </ol>
            </div>
          </template>
        </Alert>
      </div>
    </div>

    <vxe-toolbar perfect custom>
      <template #buttons>
        <div style="float: left;">&nbsp;
        </div>
        <div class="text-align-right">
          <Button icon="ios-cloud" type="text" @click="loadPartsFromExtDb" :loading="loadingOctopartParts" v-if="!readonly">{{ $t('bom_tool.load_parts_from_octopart') }}</Button>
          <Button icon="md-paper-plane" type="text" @click="showSyncErpModal = true" v-if="readonly && !hasProblem">{{ $t('bom_tool.synchronize_bom_to_erp') }}</Button>
          <Button icon="md-download" type="text" @click="handleExport">{{ $t('bom_tool.export_bom') }}</Button>
          <Button icon="md-document" type="text" @click="showRevisionsModal = true" v-if="!isForCostEstimation">{{ $t('bom_tool.revisions') }}</Button>&nbsp;
          <Button icon="md-time" type="text" @click="showActivitiesDrawer = true">{{ $t('activities') }}</Button>&nbsp;
          <Button type="warning" @click="showCreatingRevisionConfigModal = true" v-if="!readonly && !isForCostEstimation" :disabled="hasProblem">{{ $t('bom_tool.release') }}</Button>&nbsp;
          <Button type="primary" @click="openBOMItemDrawer(null)" v-if="!readonly">{{ $t('bom_tool.add_part') }}</Button>
        </div>
      </template>
    </vxe-toolbar>

    <vxe-table
      ref="bomSingleLevelViewTable"
      :data="tableData"
      :loading="loading"
      :row-class-name="bomRowClassName"
      :expand-config="{ lazy: true, loadMethod: bomRowToggleHandler }"
      size="mini"
      border
      resizable
      highlight-hover-row>
      <vxe-table-column width="42" align="center" v-if="!readonly">
        <template>
          <span class="drag-btn">
            <i class="vxe-icon--menu"></i>
          </span>
        </template>
      </vxe-table-column>
      <vxe-table-column type="seq" align="center" width="50"></vxe-table-column>
      <vxe-table-column type="expand" align="center" :title="$t('bom_tool.more')" width="54">
        <template #content="{ row }">
          <Tabs>
            <TabPane :label="$t('bom_tool.alternatives')" name="alternatives">
              <BOMItemPluginAlternates :current-part="row" :alternates="row.alternates" :readonly="readonly"></BOMItemPluginAlternates>
            </TabPane>
            <TabPane :label="$t('bom_tool.suppliers')" name="suppliers"></TabPane>
            <TabPane :label="$t('bom_tool.quality_data')" name="qualityData"></TabPane>
            <TabPane :label="$t('bom_tool.files')" name="files"></TabPane>
          </Tabs>
        </template>
      </vxe-table-column>
      <vxe-table-column field="line_quantity" align="center" :title="$t('bom_tool.qty')" width="50"></vxe-table-column>
      <vxe-table-column field="unit_name" align="center" :title="$t('bom_tool.unit')" width="50"></vxe-table-column>
      <vxe-table-column field="chinese_name" :title="$t('bom_tool.name')" :filters="[{ data: '' }]" :filter-method="fuzzyFilter" min-width="260">
        <template #default="{ row }">{{ row.chinese_name }}<span v-if="row.is_assembly" style="font-size: 90%;">&nbsp;({{ row.version || $t('bom_tool.editing') }})</span></template>
        <template #filter="{ $panel, column }">
          <input type="type" v-for="(option, index) in column.filters" :key="index" v-model="option.data" @input="$panel.changeOption($event, !!option.data, option)">
        </template>
      </vxe-table-column>
      <vxe-table-column field="part_number" :title="$t('bom_tool.part_number')" :filters="[{ data: '' }]" :filter-method="fuzzyFilter" width="130">
        <template #filter="{ $panel, column }">
          <input type="type" v-for="(option, index) in column.filters" :key="index" v-model="option.data" @input="$panel.changeOption($event, !!option.data, option)">
        </template>
      </vxe-table-column>
      <vxe-table-column field="specification" :title="$t('bom_tool.specification')" :filters="[{ data: '' }]" :filter-method="fuzzyFilter" width="420">
        <template #filter="{ $panel, column }">
          <input type="type" v-for="(option, index) in column.filters" :key="index" v-model="option.data" @input="$panel.changeOption($event, !!option.data, option)">
        </template>
        <template #default="{ row }">{{ row.specification }} {{ row.version ? `(Version: ${row.version})` : '' }}</template>
      </vxe-table-column>
      <vxe-table-column field="mpn" :title="$t('bom_tool.mfr_part_number')" :filters="[{ data: '' }]" :filter-method="fuzzyFilter" min-width="220">
        <template #filter="{ $panel, column }">
          <input type="type" v-for="(option, index) in column.filters" :key="index" v-model="option.data" @input="$panel.changeOption($event, !!option.data, option)">
        </template>
      </vxe-table-column>
      <vxe-table-column field="brand" :title="$t('bom_tool.mfr_name')" :filters="[{ data: '' }]" :filter-method="fuzzyFilter" width="160">
        <template #filter="{ $panel, column }">
          <input type="type" v-for="(option, index) in column.filters" :key="index" v-model="option.data" @input="$panel.changeOption($event, !!option.data, option)">
        </template>
      </vxe-table-column>
      <vxe-table-column field="designator" :title="$t('bom_tool.designator')" :filters="[{ data: '' }]" :filter-method="fuzzyFilter" min-width="180">
        <template #filter="{ $panel, column }">
          <input type="type" v-for="(option, index) in column.filters" :key="index" v-model="option.data" @input="$panel.changeOption($event, !!option.data, option)">
        </template>
      </vxe-table-column>
      <vxe-table-column field="item_state" :title="$t('bom_tool.status')" width="70" align="center" :filters="[{ label: 'Invalid', value: 'invalid' }, { label: 'Warn', value: 'warn' }]" :filter-multiple="false">
        <template #default="{ row }">
          <Badge :status="row.item_state === 'invalid' ? 'error' : (row.item_state === 'warn' ? 'warning' : 'success')"></Badge>
        </template>
      </vxe-table-column>
      <vxe-table-column :title="$t('bom_tool.actions')" width="100" align="center">
        <template #default="{ row }">
          <Button type="text" size="small" icon="logo-buffer" v-if="row.is_assembly" @click="handleAssemblyBomNavClick(row)"></Button>
          <Button type="text" size="small" icon="md-create" @click="openBOMItemDrawer(row)"></Button>
          <Poptip :title="$t('bom_tool.delete_item_confirmation_text')" @on-ok="handleRemoveBomItemClick(row)" :ok-text="$t('confirm')" with="500" confirm transfer>
            <Button type="text" size="small" icon="md-close" v-if="!readonly"></Button>
          </Poptip>
        </template>
      </vxe-table-column>
    </vxe-table>

    <Drawer :title="$t('bom_tool.bom_item_form_drawer_title')" width="450" v-model="showBOMItemDrawer">
      <BomItemForm :bom-item-data="currentBomItem" :bom-id="bomId" @bom-item-saved="handleBomItemSaved" :readonly="readonly" :is-new="isNewItem"></BomItemForm>
    </Drawer>

    <Modal :title="$t('bom_tool.synchronize_bom_to_erp')" v-model="showSyncErpModal" width="90%">
      <BOMSync2ERPConfigForm ref="bomMigrationForm"
                            :current-bom="bom"
                            :running.sync="runningBOMSync"
                            wrapped-in-modal>
      </BOMSync2ERPConfigForm>
      <div slot="footer">
        <Button type="primary" @click="handleSyncToERPSubmit" :loading="runningBOMSync">{{ $t('bom_tool.migration.start_migration') }}</Button>
      </div>
    </Modal>

    <Modal :title="$t('bom_tool.revisions')" v-model="showRevisionsModal" width="1000">
      <BOMRevisionList :bom-id="bomId" :bom-version="version"></BOMRevisionList>
    </Modal>

    <Modal :title="$t('bom_tool.release_revision_and_log')"
           v-model="showCreatingRevisionConfigModal"
           :loading="creatingRevision">
      <BOMNewRevisionForm ref="bomNewRevisionForm"
                          :bom-id="bomId"
                          :current-version="version"
                          @on-saving-finished="creatingRevision = false"
                          wrapped-in-modal>
      </BOMNewRevisionForm>
      <div slot="footer">
        <Button type="primary" @click="handleCreateNewVersionSubmit" :loading="creatingRevision">{{ $t('bom_tool.create_new_version') }}</Button>
      </div>
    </Modal>

    <Drawer width="450" :title="$t('activities')" v-model="showActivitiesDrawer">
      <BOMChangeAudits :bom-id="bomId"></BOMChangeAudits>
    </Drawer>
  </div>
</template>

<script>
import Sortable from 'sortablejs'
import { mapActions } from 'vuex'
import { downloadBOM } from '@/api/bom'
import { saveAs } from 'file-saver'
import BomItemForm from '@/view/bom/components/forms/bom-item-form'
import BOMNewRevisionForm from '@/view/bom/components/forms/bom-new-revision-form'
import BOMRevisionList from '@/view/bom/components/bom-revision-list'
import BOMSync2ERPConfigForm from '@/view/bom/components/forms/bom-sync-2-erp-config-form'
import { BOMItemPluginAlternates, BOMChangeAudits } from '@/view/bom/components/widgets'
export default {
  name: 'BOMSingleLevelView',
  components: {
    BomItemForm,
    BOMNewRevisionForm,
    BOMRevisionList,
    BOMSync2ERPConfigForm,
    BOMItemPluginAlternates,
    BOMChangeAudits
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    bom: {
      type: Object,
      default () {
        return {}
      }
    },
    bomItems: {
      type: Array,
      default () {
        return []
      }
    },
    readonly: {
      type: Boolean,
      default: false
    },
    isForCostEstimation: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    bomId () {
      return this.bom?.id
    },
    version () {
      return this.bom?.version
    },
    problemProsecutorResults () {
      let result = {}
      for (const i in this.prosecutorResult) {
        result[i] = _.filter(this.prosecutorResult[i], x => x.status !== 'info')
      }
      return result
    },
    hasProblem () {
      for (const i in this.prosecutorResult) {
        let result = _.filter(this.prosecutorResult[i], x => x.status === 'error')
        if (result.length > 0) {
          return true
        }
      }
      return false
    }
  },
  data () {
    return {
      tableData: [],
      showProsecutorDetail: {},
      showBOMItemDrawer: false,
      showSyncErpModal: false,
      showRevisionsModal: false,
      showActivitiesDrawer: false,
      showCreatingRevisionConfigModal: false,
      errorFound: false,
      creatingRevision: false,
      currentBomItem: {},
      prosecutorResult: null,
      runningBOMSync: false,
      isNewItem: false,
      loadingOctopartParts: false,
      secIds: [],
      prosecutorCheckers: [
        'integrity_checker',
        'part_number_checker',
        'status_checker',
        'designator_quantity_checker'
      ]
    }
  },
  mounted () {
    if (!this.bom.setting || this.bom.setting.bom_type !== 'cost_estimation_use') {
      let params = { bomId: this.bomId, checkers: this.prosecutorCheckers }
      if (this.readonly && this.$route.query.version) {
        params = Object.assign({}, params, { version: this.$route.query.version })
      }
      this.runBOMProsecutorChecks(params).then(resp => {
        this.prosecutorResult = Object.fromEntries(Object.entries(resp.data).filter(([_, v]) => v.length > 0))
      })
    }
  },
  created () {
    this.handleMakeTableData()
    this.rowDrop()
  },
  beforeDestroy () {
    if (this.sortableTable) {
      this.sortableTable.destroy()
    }
  },
  methods: {
    ...mapActions([
      'runBOMProsecutorChecks',
      'getBomItemPluginData',
      'deleteBomItem',
      'moveBomItemSequence',
      'loadPartsFromOctopart'
    ]),
    downloadBOM,
    saveAs,
    handleMakeTableData() {
      this.tableData = _.cloneDeep(this.bomItems).sort((a, b) => a.sort_weight - b.sort_weight)
      this.secIds = this.tableData.map(r => r.id)
    },
    fuzzyFilter ({ option, row, column }) {
      return row[column.property] && row[column.property].toLowerCase().indexOf(option.data.toLowerCase()) > -1
    },
    bomRowClassName ({ row }) {
      let classes = [row.is_assembly ? 'is-assembly-part' : '']

      if (row.item_state === 'invalid') {
        this.errorFound = true
        classes.push('error')
      }
      return classes
    },
    openBOMItemDrawer (row) {
      this.isNewItem = !row
      this.currentBomItem = _.cloneDeep(row) || {}
      this.showBOMItemDrawer = true
    },
    loadPartsFromExtDb () {
      this.loadingOctopartParts = true
      this.loadPartsFromOctopart(this.bomId).then(resp => {
        window.location.reload()
      }).finally(() => {
        this.loadingOctopartParts = false
      })
    },
    handleExport () {
      this.downloadBOM(this.bomId).then(resp => {
        let blob = new Blob([resp.data], { type: resp.headers['content-type'] })
        this.saveAs(blob, resp.headers['content-disposition'].split('filename=')[1])
      })
    },
    handleCreateNewVersionSubmit () {
      this.creatingRevision = true
      let me = this
      this.$refs['bomNewRevisionForm'].handleSubmit(function (resp) {
        me.creatingRevision = false
        me.showCreatingRevisionConfigModal = false
      })
    },
    handleSyncToERPSubmit () {
      this.$refs['bomMigrationForm'].doMigration()
    },
    handleAssemblyBomNavClick (row) {
      let option = {
        name: 'menu.bom.view',
        params: { bomId: row.assembly_bom_id }
      }

      if (row.version && row.version.toLowerCase() !== 'draft') {
        option.query = { version: row.version }
      }

      const route = this.$router.resolve(option)
      window.open(route.href, '_blank')
    },
    handleRemoveBomItemClick (row) {
      this.deleteBomItem(row.id)
    },
    handleBomItemSaved () {
      // close BOM item form drawer
      this.showBOMItemDrawer = false
    },
    bomRowToggleHandler ({ row }) {
      const query = row.version_id ? { bom_item_version_id: row.version_id } : { bom_item_id: row.id }
      return this.getBomItemPluginData({ plugin: 'alternates', query: query }).then(resp => {
        row.alternates = resp.data
      })
    },
    rowDrop () {
      this.$nextTick(() => {
        const xTable = this.$refs.bomSingleLevelViewTable
        this.sortableTable = Sortable.create(xTable.$el.querySelector('.body--wrapper>.vxe-table--body tbody'), {
          handle: '.drag-btn',
          onEnd: ({ newIndex, oldIndex }) => {
            const currRow = this.secIds.splice(oldIndex, 1)[0]
            this.secIds.splice(newIndex, 0, currRow)
            this.moveBomItemSequence({ ids: this.secIds })
          }
        })
      })
    },
    handleProsecutorDetailDisplay (code) {
      this.$set(this.showProsecutorDetail, code, !this.showProsecutorDetail[code])
    }
  },
  watch: {
    bomItems: function () {
      this.handleMakeTableData()
    }
  }
}
</script>

<style lang="less">
.bom-view--single-level-view {
  .vxe-body--row.is-assembly-part { font-weight: bold; }
  .ivu-badge-status-text { display: none; }
  .drag-btn { cursor: move; font-size: 12px; }
}
.vxe-toolbar.is--perfect {
  padding: 0 8px;
}
</style>
